/**
 * @file     useFormMemory.ts
 * @purpose  Persists common lead-form fields (name, email, phone, address) in sessionStorage so users don't re-type across funnels.
 */
'use client';

import { useCallback, useRef } from 'react';

const STORAGE_KEY = 'relta_form_memory';

export type FormMemoryFields = {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
};

const PERSISTED_KEYS: (keyof FormMemoryFields)[] = ['name', 'firstName', 'lastName', 'email', 'phone', 'address'];

function readMemory(): FormMemoryFields {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const result: FormMemoryFields = {};
    for (const key of PERSISTED_KEYS) {
      if (typeof parsed[key] === 'string' && parsed[key]) {
        result[key] = parsed[key];
      }
    }
    return result;
  } catch {
    return {};
  }
}

function writeMemory(fields: FormMemoryFields): void {
  if (typeof window === 'undefined') return;
  try {
    const existing = readMemory();
    const merged = { ...existing };
    for (const key of PERSISTED_KEYS) {
      if (fields[key] !== undefined && fields[key] !== '') {
        merged[key] = fields[key];
      }
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // sessionStorage unavailable, fail silently
  }
}

/**
 * Returns helpers to read and save common form fields from sessionStorage.
 * Call `getMemory()` to get stored values. Call `saveField(key, value)` when a field changes.
 */
export function useFormMemory() {
  const cacheRef = useRef<FormMemoryFields | null>(null);

  const getMemory = useCallback((): FormMemoryFields => {
    if (cacheRef.current === null) {
      cacheRef.current = readMemory();
    }
    return cacheRef.current;
  }, []);

  const saveField = useCallback((key: keyof FormMemoryFields, value: string) => {
    if (!value) return;
    if (cacheRef.current) {
      cacheRef.current[key] = value;
    }
    writeMemory({ [key]: value });
  }, []);

  const saveFields = useCallback((fields: FormMemoryFields) => {
    const nonEmpty: FormMemoryFields = {};
    for (const key of PERSISTED_KEYS) {
      if (fields[key]) {
        nonEmpty[key] = fields[key];
        if (cacheRef.current) cacheRef.current[key] = fields[key];
      }
    }
    writeMemory(nonEmpty);
  }, []);

  return { getMemory, saveField, saveFields };
}
